import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { downloadFile } from '../../../../shared/helpers'

import Btn from '../../../../UI/Btn'

export const HANDBOOK_FILE_NAME = 'Skills4Retail-Handbuch.pdf'

/**
 * Öffnet das Handbuch zur Lernstrecke in einem neuen Tab (Anforderung #187).
 *
 * Die Datei liegt im Backend hinter einer Anmeldung
 * (`GET /api/private/course/handbook`) und nicht im `public`-Ordner — ein
 * Link mit `href` schickte den Anmelde-Kopfeintrag nicht mit. Deshalb dasselbe
 * Muster wie bei den Dokumenten: angemeldet als Blob holen, als lokale Adresse
 * öffnen.
 *
 * **Der Tab geht vor dem Abruf auf, nicht danach.** Ein `window.open` erst nach
 * der Antwort zählt für den Browser nicht mehr als Folge des Klicks; Safari und
 * Firefox blockieren es dann als Popup — bei 7 MB und einer langsamen
 * Schulleitung dauert der Abruf spürbar. Der leere Tab bekommt die Adresse,
 * sobald die Datei da ist. Lässt der Browser gar keinen Tab zu, wird die Datei
 * heruntergeladen, statt dass der Klick wortlos verpufft.
 */
export function openHandbook({ loadingText = '' } = {}) {
	const tab = window.open('', '_blank')

	if (tab) {
		// Der neue Tab soll nicht auf die Anwendung zurückgreifen können.
		tab.opener = null
		try {
			tab.document.title = HANDBOOK_FILE_NAME
			if (loadingText) tab.document.body.textContent = loadingText
		} catch (err) {
			// Ohne Zugriff auf den leeren Tab bleibt er eben leer, bis die Datei da ist.
		}
	}

	return http
		.get(TApi.COURSE_HANDBOOK, { responseType: 'blob' })
		.then(({ data }) => {
			const blob = new Blob([data], { type: 'application/pdf' })

			if (tab && !tab.closed) tab.location.href = URL.createObjectURL(blob)
			else downloadFile(HANDBOOK_FILE_NAME, blob)
		})
		.catch(() => {
			// Die Meldung kommt aus shared/http; der leere Tab soll nicht stehen bleiben.
			if (tab && !tab.closed) tab.close()
		})
}

/**
 * Der Knopf „Handbuch Skills4Retail" rechts oben auf der Skills4Retail-Seite.
 *
 * Er prüft selbst keine Rolle: Er steht nur dort, wo die Seite ohnehin nur für
 * die passenden Rollen erscheint (`pages/Concept` für Schüler:innen und
 * Lehrkräfte, die Auswertung für die Verwaltung). Eine eigene Rechnung hier
 * liefe früher oder später neben der des Menüpunkts auseinander.
 */
export default function HandbookButton({ className }) {
	const { t } = useTranslation()
	const [laedt, setLaedt] = useState(false)

	const oeffnen = () => {
		setLaedt(true)
		openHandbook({ loadingText: t('The handbook is loading …') }).finally(() => setLaedt(false))
	}

	return (
		<Btn
			theme="primary"
			icon="document"
			iconColor="#fff"
			noFillIcon
			iconSize={{ w: 18, h: 18 }}
			className={className}
			onClick={oeffnen}
			loading={laedt}
		>
			<span title={t('Opens the handbook on the Skills4Retail course and the concept as a PDF in a new tab.')}>
				{t('Skills4Retail handbook')}
			</span>
		</Btn>
	)
}

HandbookButton.propTypes = {
	className: PropTypes.string
}
